import { useState, useRef, useEffect, type ChangeEvent, type FormEvent } from 'react';
import { Building2, CreditCard, Bell, Loader2, Check, Upload, BarChart3 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePartnerSettings, useUpdateSettings, useUploadLogo } from '@/hooks/useSettings';
import { Card, CardHeader, CardTitle, CardContent, ErrorState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { isValidCnpj, maskCnpj } from '@/lib/cnpj';
import { lookupCep } from '@mustauto/utils';

type Tab = 'company' | 'billing' | 'notifications' | 'reports';

const TABS: { id: Tab; label: string; icon: typeof Building2 }[] = [
  { id: 'company', label: 'Dados da Empresa', icon: Building2 },
  { id: 'billing', label: 'Faturamento', icon: CreditCard },
  { id: 'notifications', label: 'Notificações', icon: Bell },
  { id: 'reports', label: 'Relatórios', icon: BarChart3 },
];

const UF_OPTIONS = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

function maskZip(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function SettingsPage() {
  const { user } = useAuth();
  const { data: settings, isLoading, isError, error, refetch } = usePartnerSettings();
  const updateSettings = useUpdateSettings();
  const uploadLogo = useUploadLogo();

  const [activeTab, setActiveTab] = useState<Tab>('company');
  const [saved, setSaved] = useState(false);

  // Company fields
  const [legalName, setLegalName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [cnpjError, setCnpjError] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Billing fields
  const [zip, setZip] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [billingEmail, setBillingEmail] = useState('');

  // Notification fields
  const [weeklyReport, setWeeklyReport] = useState(true);
  const [campaignAlerts, setCampaignAlerts] = useState(true);
  const [opportunities, setOpportunities] = useState(true);
  const [osCategorySummary, setOsCategorySummary] = useState(false);

  // Populate fields when settings load
  useEffect(() => {
    if (!settings) return;
    setLegalName(settings.legalName ?? '');
    setCnpj(settings.cnpj ?? '');
    setLogoPreview(settings.logoUrl);

    const addr = settings.billingAddress;
    if (addr) {
      setZip(addr.zip ?? '');
      setStreet(addr.street ?? '');
      setNumber(addr.number ?? '');
      setComplement(addr.complement ?? '');
      setNeighborhood(addr.neighborhood ?? '');
      setCity(addr.city ?? '');
      setState(addr.state ?? '');
    }
    setBillingEmail(settings.billingEmail ?? '');

    const prefs = settings.notificationPrefs;
    setWeeklyReport(prefs.weekly_report);
    setCampaignAlerts(prefs.campaign_alerts);
    setOpportunities(prefs.opportunities);
    setOsCategorySummary(prefs.os_category_summary);
  }, [settings]);

  function handleCnpjChange(e: ChangeEvent<HTMLInputElement>) {
    const masked = maskCnpj(e.target.value);
    setCnpj(masked);
    const digits = masked.replace(/\D/g, '');
    if (digits.length === 14) {
      setCnpjError(isValidCnpj(digits) ? '' : 'CNPJ inválido');
    } else {
      setCnpjError('');
    }
  }

  function handleLogoClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
    uploadLogo.mutate(file);
  }

  async function handleZipBlur() {
    const found = await lookupCep(zip);
    if (!found) return;
    // Só sobrescreve o que veio preenchido: CEP de cidade inteira não traz
    // logradouro/bairro e não pode apagar o que o parceiro já digitou.
    if (found.street) setStreet(found.street);
    if (found.neighborhood) setNeighborhood(found.neighborhood);
    if (found.city) setCity(found.city);
    if (found.state) setState(found.state);
  }

  function handleSave(e: FormEvent) {
    e.preventDefault();

    if (activeTab === 'company') {
      const digits = cnpj.replace(/\D/g, '');
      if (digits && !isValidCnpj(digits)) {
        setCnpjError('CNPJ inválido');
        return;
      }
      // Persiste só dígitos — a máscara é coisa de display.
      updateSettings.mutate(
        { legalName, cnpj: digits || undefined },
        { onSuccess: showSaved },
      );
    } else if (activeTab === 'billing') {
      updateSettings.mutate(
        {
          billingAddress: { street, number, complement, neighborhood, city, state, zip: zip.replace(/\D/g, '') },
          billingEmail: billingEmail || undefined,
        },
        { onSuccess: showSaved },
      );
    } else {
      updateSettings.mutate(
        {
          notificationPrefs: {
            weekly_report: weeklyReport,
            campaign_alerts: campaignAlerts,
            opportunities,
            os_category_summary: osCategorySummary,
          },
        },
        { onSuccess: showSaved },
      );
    }
  }

  function showSaved() {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Sem isto o formulario renderizava com os campos vazios quando a busca
  // falhava — e salvar dali gravaria os vazios por cima dos dados reais.
  if (isError && !settings) {
    return (
      <ErrorState
        title="Não foi possível carregar suas configurações"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Configurações</h1>
        <p className="text-sm text-muted-foreground">
          Gerencie dados da empresa, faturamento e notificações
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-border bg-muted p-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSave}>
        {/* Tab: Dados da Empresa */}
        {activeTab === 'company' && (
          <Card>
            <CardHeader>
              <CardTitle>Dados da Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Logo */}
                <div>
                  <label className="mb-2 block text-sm text-muted-foreground">Logo</label>
                  {/* Sem `aria-label` o botao so tinha nome quando havia
                      preview (vindo do alt da img); vazio, era um botao mudo —
                      e e o unico jeito de subir a logo da empresa. */}
                  <button
                    type="button"
                    aria-label={logoPreview ? 'Trocar logo da empresa' : 'Enviar logo da empresa'}
                    onClick={handleLogoClick}
                    className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-border bg-muted hover:border-foreground/40 transition-colors"
                  >
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="h-full w-full object-cover" />
                    ) : (
                      <Upload className="h-6 w-6 text-muted-foreground" />
                    )}
                    {uploadLogo.isPending && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full" aria-hidden="true">
                        <Loader2 className="h-5 w-5 animate-spin text-white" />
                      </div>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>

                {/* Legal Name */}
                <div>
                  <label className="mb-1 block text-sm text-muted-foreground">Razão Social</label>
                  <input
                    type="text"
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    placeholder="Nome completo da empresa"
                    className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                  />
                </div>

                {/* CNPJ */}
                <div>
                  <label className="mb-1 block text-sm text-muted-foreground">CNPJ</label>
                  <input
                    type="text"
                    value={cnpj}
                    onChange={handleCnpjChange}
                    placeholder="00.000.000/0000-00"
                    className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                  />
                  {cnpjError && <p className="mt-1 text-xs text-red-500 dark:text-red-400">{cnpjError}</p>}
                </div>

                {/* Read-only fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm text-muted-foreground">Tier</label>
                    <input
                      type="text"
                      value={settings?.tier ?? '--'}
                      readOnly
                      className="w-full rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-muted-foreground">Nome do parceiro</label>
                    <input
                      type="text"
                      value={settings?.name ?? '--'}
                      readOnly
                      className="w-full rounded-lg border border-border bg-muted/50 px-4 py-3 text-sm text-muted-foreground cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab: Faturamento */}
        {activeTab === 'billing' && (
          <Card>
            <CardHeader>
              <CardTitle>Faturamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* CEP */}
                <div>
                  <label className="mb-1 block text-sm text-muted-foreground">CEP</label>
                  <input
                    type="text"
                    value={maskZip(zip)}
                    onChange={(e) => setZip(e.target.value)}
                    onBlur={handleZipBlur}
                    placeholder="00000-000"
                    className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                  />
                </div>

                {/* Street + Number */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-sm text-muted-foreground">Rua</label>
                    <input
                      type="text"
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-muted-foreground">Número</label>
                    <input
                      type="text"
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                    />
                  </div>
                </div>

                {/* Complement + Neighborhood */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm text-muted-foreground">Complemento</label>
                    <input
                      type="text"
                      value={complement}
                      onChange={(e) => setComplement(e.target.value)}
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-muted-foreground">Bairro</label>
                    <input
                      type="text"
                      value={neighborhood}
                      onChange={(e) => setNeighborhood(e.target.value)}
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                    />
                  </div>
                </div>

                {/* City + State */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-sm text-muted-foreground">Cidade</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-muted-foreground">Estado</label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                    >
                      <option value="">UF</option>
                      {UF_OPTIONS.map((uf) => (
                        <option key={uf} value={uf}>{uf}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Billing Email */}
                <div>
                  <label className="mb-1 block text-sm text-muted-foreground">Email de cobrança</label>
                  <input
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    placeholder="financeiro@empresa.com.br"
                    className="w-full rounded-lg border border-border bg-muted px-4 py-3 text-sm"
                  />
                </div>

                {/* Info card */}
                <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm text-muted-foreground">
                  Faturas e boletos são gerenciados pela equipe MustAuto. Para dúvidas, contato:{' '}
                  <span className="font-medium text-foreground">financeiro@mustauto.com.br</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab: Notificações */}
        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle>Notificações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <ToggleRow
                  checked={weeklyReport}
                  onChange={setWeeklyReport}
                  label="Relatório semanal de campanhas"
                  description="Receba um resumo por email toda segunda-feira"
                />
                <ToggleRow
                  checked={campaignAlerts}
                  onChange={setCampaignAlerts}
                  label="Alertas de campanha"
                  description="Notificação quando o orçamento atingir 80%"
                />
                <ToggleRow
                  checked={opportunities}
                  onChange={setOpportunities}
                  label="Novas oportunidades de mercado"
                  description="Oportunidades identificadas na sua categoria e região"
                />
                <ToggleRow
                  checked={osCategorySummary}
                  onChange={setOsCategorySummary}
                  label="Resumo de OS da categoria"
                  description="Resumo diário de OS concluídas na sua região"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab: Relatórios */}
        {activeTab === 'reports' && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <CardTitle>Relatórios Agendados</CardTitle>
                <Badge variant="secondary">Em breve</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-4 rounded-lg border border-border bg-muted/50 p-5">
                  <BarChart3 className="mt-0.5 h-8 w-8 text-primary/60 shrink-0" />
                  <div>
                    <p className="text-sm font-medium">
                      Envie automaticamente um relatório PDF para seu email toda semana ou mês.
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Configure a frequência, os dados incluídos e os destinatários. Ideal para
                      manter sua equipe atualizada sem precisar acessar o portal.
                    </p>
                    <button
                      type="button"
                      disabled
                      className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground opacity-50 cursor-not-allowed"
                    >
                      Configurar
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save button */}
        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={updateSettings.isPending}
            className="rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-foreground/90 disabled:opacity-50"
          >
            {updateSettings.isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Salvando...
              </span>
            ) : (
              'Salvar alterações'
            )}
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
              <Check className="h-4 w-4" />
              Salvo com sucesso
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-4">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-muted-foreground/30'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-background transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
